// A textured sphere, ray projected in one small draw call. No 3D engine or
// animation dependency: the surrounding scene owns the clock and destination.
import { createLogger } from "@/lib/logger";

const logger = createLogger("first-run-globe");
const VERTEX = `
attribute vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const FRAGMENT = `
precision highp float;
uniform sampler2D atlas;
uniform vec3 view;
uniform vec3 sphere;
uniform vec2 viewport;
const float PI = 3.14159265359;
void main() {
  vec2 point = (gl_FragCoord.xy - vec2(sphere.x, viewport.y-sphere.y))/sphere.z;
  float radius = length(point);
  if (radius > 1.0) discard;
  float a = view.z;
  vec2 p = mat2(cos(a), sin(a), -sin(a), cos(a)) * point;
  vec3 normal = vec3(p, sqrt(max(0.0, 1.0 - dot(p, p))));
  float lon = view.x;
  float lat = view.y;
  vec3 east = vec3(cos(lon), 0.0, -sin(lon));
  vec3 north = vec3(-sin(lat)*sin(lon), cos(lat), -sin(lat)*cos(lon));
  vec3 front = vec3(cos(lat)*sin(lon), sin(lat), cos(lat)*cos(lon));
  vec3 world = east*normal.x + north*normal.y + front*normal.z;
  vec2 uv = vec2(atan(world.x, world.z)/(2.0*PI)+0.5, asin(clamp(world.y,-1.0,1.0))/PI+0.5);
  vec3 color = texture2D(atlas, uv).rgb;
  float light = max(0.0, dot(normalize(vec3(-0.45,0.65,0.85)), vec3(point,normal.z)));
  color *= 0.32 + 0.68*light;
  float rim = pow(1.0-normal.z, 4.0);
  color += vec3(0.26,0.36,0.33)*rim*0.45;
  color += vec3(0.5,0.22,0.08)*pow(max(point.y,0.0),6.0)*rim*0.45;
  gl_FragColor = vec4(color, 1.0-smoothstep(0.995,1.0,radius));
}`;

export interface GlobeRenderer {
  draw: (
    longitude: number,
    latitude: number,
    roll: number,
    radius: number,
    horizon: number
  ) => void;
  dispose: () => void;
}

export function createGlobeRenderer(canvas: HTMLCanvasElement): GlobeRenderer | null {
  const gl = canvas.getContext("webgl", { alpha: true, antialias: false, depth: false });
  if (!gl) return null;
  const shaders: WebGLShader[] = [];
  const program = gl.createProgram();
  const buffer = gl.createBuffer();
  const texture = gl.createTexture();
  const image = new Image();
  let disposed = false;
  let loaded = false;
  let lastView = [20, -20, 10, 550, 600];
  let density = 1;
  let observer: ResizeObserver | null = null;

  const dispose = () => {
    disposed = true;
    observer?.disconnect();
    image.onload = null;
    image.onerror = null;
    shaders.forEach((shader) => gl.deleteShader(shader));
    gl.deleteProgram(program);
    gl.deleteBuffer(buffer);
    gl.deleteTexture(texture);
  };
  if (!program || !buffer || !texture) {
    dispose();
    return null;
  }
  for (const [type, source] of [
    [gl.VERTEX_SHADER, VERTEX],
    [gl.FRAGMENT_SHADER, FRAGMENT],
  ] as const) {
    const shader = gl.createShader(type);
    if (!shader) {
      dispose();
      return null;
    }
    shaders.push(shader);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      logger.warn("Globe shader unavailable; using illustration", {
        detail: gl.getShaderInfoLog(shader),
      });
      dispose();
      return null;
    }
    gl.attachShader(program, shader);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    logger.warn("Globe program unavailable; using illustration", {
      detail: gl.getProgramInfoLog(program),
    });
    dispose();
    return null;
  }
  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, "position");
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  const view = gl.getUniformLocation(program, "view");
  const sphere = gl.getUniformLocation(program, "sphere");
  const viewport = gl.getUniformLocation(program, "viewport");
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  const draw: GlobeRenderer["draw"] = (longitude, latitude, roll, radius, horizon) => {
    lastView = [longitude, latitude, roll, radius, horizon];
    if (disposed || !loaded || gl.isContextLost()) return;
    gl.uniform3f(
      view,
      (longitude * Math.PI) / 180,
      (latitude * Math.PI) / 180,
      (roll * Math.PI) / 180
    );
    gl.uniform3f(sphere, canvas.width / 2, (horizon + radius) * density, radius * density);
    gl.uniform2f(viewport, canvas.width, canvas.height);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };
  const resize = () => {
    // Rasterize only visible screen pixels, not an enormous offscreen sphere.
    // This keeps a large horizon sharp without allocating its whole diameter.
    density = Math.min(
      devicePixelRatio || 1,
      2,
      4096 / Math.max(canvas.clientWidth, canvas.clientHeight)
    );
    const width = Math.ceil(canvas.clientWidth * density);
    const height = Math.ceil(canvas.clientHeight * density);
    if (!width || !height) return;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
    }
    draw(lastView[0], lastView[1], lastView[2], lastView[3], lastView[4]);
  };
  observer = new ResizeObserver(resize);
  observer.observe(canvas);
  image.onload = () => {
    if (disposed || gl.isContextLost()) return;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
    loaded = true;
    canvas.dataset.globeRendered = "true";
    resize();
  };
  image.src = "/ui/brand-kit/web/chicky-world-map.webp?v=graphite-hires";
  return { draw, dispose };
}
